import React from 'react';
import { FormControl, Select, MenuItem, SelectChangeEvent, Chip, Box, Typography, InputLabel } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../hooks/redux.hooks';
import { setSelectedFiscalYear } from '../../store/slices/authSlice';
import { useLanguage } from '../../contexts/LanguageContext';
import axiosInstance from '../../services/axiosInstance';

const translations = {
  en: { fiscalYear: 'Fiscal Year', current: 'Current', closed: 'Closed' },
  ar: { fiscalYear: 'السنة المالية', current: 'الحالية', closed: 'مقفلة' },
};

interface FiscalYearSelectorProps {
  value?: number | null;
  onChange?: (id: number | null) => void;
  required?: boolean;
  showLabel?: boolean;
  variant?: 'header' | 'form';
}

const FiscalYearSelector: React.FC<FiscalYearSelectorProps> = ({ 
  value, 
  onChange, 
  required = false,
  showLabel = true,
  variant = 'form'
}) => {
  const dispatch = useAppDispatch();
  const { availableFiscalYears, selectedFiscalYearId, currentFiscalYearId } = useAppSelector((state) => state.auth);
  const { language } = useLanguage();
  const t = translations[language];

  // استخدم props إذا مُررت، وإلا استخدم Redux
  const currentValue = value !== undefined ? value : selectedFiscalYearId;

  const handleChange = (event: SelectChangeEvent<number | ''>) => {
    const newValue = event.target.value === '' ? null : Number(event.target.value);
    
    if (onChange) {
      onChange(newValue);
    } else {
      if (newValue) {
        dispatch(setSelectedFiscalYear(newValue));
        
        // ✅ إرسال Header لكل الـ Requests القادمة
        axiosInstance.defaults.headers.common['X-Fiscal-Year-Id'] = newValue.toString();
        
        // ✅ Refresh الصفحة لتحميل البيانات الجديدة (فقط في variant = header)
        if (variant === 'header') {
          window.location.reload();
        }
      }
    }
  };

  // إذا كان variant = header ولا يوجد سنوات نهائياً، لا تعرض شيء
  if (variant === 'header' && (!availableFiscalYears || availableFiscalYears.length === 0)) {
    return null;
  }

  // Header variant (للـ AppBar)
  if (variant === 'header') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CalendarMonth sx={{ color: 'inherit', fontSize: 20 }} />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={currentValue || ''}
            onChange={handleChange}
            sx={{
              color: 'inherit',
              '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.7)' },
              '.MuiSvgIcon-root': { color: 'inherit' },
            }}
          >
            {availableFiscalYears.map((fy) => (
              <MenuItem key={fy.fiscalPeriodId} value={fy.fiscalPeriodId}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>{fy.periodName}</Typography>
                  {fy.fiscalPeriodId === currentFiscalYearId && (
                    <Chip label={t.current} size="small" color="success" sx={{ height: 20, fontSize: 10 }} />
                  )}
                  {fy.isClosed && (
                    <Chip label={t.closed} size="small" color="warning" sx={{ height: 20, fontSize: 10 }} />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  // Form variant (للنماذج)
  return (
    <FormControl fullWidth size="small" required={required}>
      {showLabel && <InputLabel>{t.fiscalYear}</InputLabel>}
      <Select
        value={currentValue || ''}
        onChange={handleChange}
        label={showLabel ? t.fiscalYear : undefined}
      >
        {availableFiscalYears.map((fy) => (
          <MenuItem key={fy.fiscalPeriodId} value={fy.fiscalPeriodId}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography>{fy.periodName}</Typography>
              {fy.fiscalPeriodId === currentFiscalYearId && (
                <Chip label={t.current} size="small" color="success" sx={{ height: 20, fontSize: 10 }} />
              )}
              {fy.isClosed && (
                <Chip label={t.closed} size="small" color="warning" sx={{ height: 20, fontSize: 10 }} />
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default FiscalYearSelector;